import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Row, Col, Alert, InputGroup, Card, ListGroup, Badge } from 'react-bootstrap';
import { Upload, Image as ImageIcon, X, Tag, Package, Briefcase } from 'lucide-react';
import productService, { PRODUCT_TYPES } from '../../services/productService';
import { useAuth } from '../../contexts/AuthContext';

const CreateProductModal = ({ 
  show, 
  onHide, 
  categories, 
  onSuccess, 
  product = null 
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  
  // États pour l'autocomplétion des catégories
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [showCategorySuggestions, setShowCategorySuggestions] = useState(false);

  const {user} = useAuth();
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    categoryName: '',
    productType: user.productAccessType === 'SERVICE' ? PRODUCT_TYPES.SERVICE : PRODUCT_TYPES.STOCKABLE, // ✅ NOUVEAU : Type par défaut
    stock: '',
    sku: '',
    barcode: '',
    costPrice: '',
    minThreshold: '5',
    maxThreshold: '50'
  });

  const isEditing = !!product;

  useEffect(() => {
    if (show) {
      

      
      // Logique originale avec categoryName
      let defaultCategoryName = '';
      let foundCategory = null;
      
      if (product?.categoryName || product?.category_name) {
        defaultCategoryName = product.categoryName || product.category_name;
        foundCategory = categories?.find(cat => cat.name === defaultCategoryName);
      } else if (categories && categories.length > 0) {
        defaultCategoryName = categories[0].name;
        foundCategory = categories[0];
      }
      
      setSelectedCategory(foundCategory);
      
      // ✅ CORRECTION : Logique améliorée pour déterminer le productType
      let initialProductType;
      
      if (product) {
        // MODE ÉDITION : Toujours conserver le type existant du produit
        initialProductType = product?.productType || product?.product_type || PRODUCT_TYPES.STOCKABLE;
      } else {
        // MODE CRÉATION : Définir le type par défaut selon l'accès utilisateur
        if (user.productAccessType === 'SERVICE') {
          initialProductType = PRODUCT_TYPES.SERVICE;
        } else if (user.productAccessType === 'STOCKABLE') {
          initialProductType = PRODUCT_TYPES.STOCKABLE;
        } else {
          // Accès ALL : Ne pas écraser le state, garder la valeur actuelle
          // Cela permet de conserver le choix fait par l'utilisateur via les radio buttons
          initialProductType = formData.productType || PRODUCT_TYPES.STOCKABLE;
        }
      }
      
      setFormData({
        name: product?.name || '',
        description: product?.description || '',
        price: product?.price?.toString() || '',
        categoryName: defaultCategoryName,
        // ✅ CORRECTION: Utiliser la logique améliorée au lieu de la ternaire
        productType: initialProductType,
        imageUrl: product?.imageUrl || null, // ✅ AJOUTER pour préserver l'image
        stock: product?.currentStock?.toString() || product?.stock?.toString() || '',
        sku: product?.sku || '',
        barcode: product?.barcode || '',
        costPrice: product?.costPrice?.toString() || product?.cost_price?.toString() || '',
        // ✅ CORRECTION: Récupérer les vraies valeurs des seuils (pas les valeurs par défaut)
        minThreshold: product?.minThreshold?.toString() || product?.min_threshold?.toString() || '',
        maxThreshold: product?.maxThreshold?.toString() || product?.max_threshold?.toString() || ''
      });

      if (product?.imageUrl) {
        setImagePreview(product.imageUrl);
      }

      setError('');
      setSelectedImage(null);
      setShowCategorySuggestions(false);
    }
  }, [show, product, categories]);

  // ✅ NOUVEAU : Gérer le changement de type de produit
  const handleProductTypeChange = (newType) => {
    setFormData(prev => ({
      ...prev,
      productType: newType,
      // Reset des champs de stock si on passe en service
      ...(newType === PRODUCT_TYPES.SERVICE && {
        stock: '',
        minThreshold: '',
        maxThreshold: ''
      })
    }));
  };

  // Gestion de l'autocomplétion des catégories (logique existante)
  const handleCategoryInputChange = (value) => {
    setFormData(prev => ({
      ...prev,
      categoryName: value
    }));

    if (value.trim() === '') {
      setFilteredCategories([]);
      setShowCategorySuggestions(false);
      setSelectedCategory(null);
      return;
    }

    const filtered = categories?.filter(cat =>
      cat.name.toLowerCase().includes(value.toLowerCase())
    ) || [];

    setFilteredCategories(filtered);
    setShowCategorySuggestions(true);

    const exactMatch = categories?.find(cat => 
      cat.name.toLowerCase() === value.toLowerCase()
    );
    setSelectedCategory(exactMatch || null);
  };

  const selectCategory = (category) => {
    setFormData(prev => ({
      ...prev,
      categoryName: category.name
    }));
    setSelectedCategory(category);
    setShowCategorySuggestions(false);
  };

  // Gestion des images (logique existante)
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      const maxSize = 5 * 1024 * 1024; // 5MB

      if (!allowedTypes.includes(file.type)) {
        setError('Format d\'image non supporté. Utilisez JPG, PNG ou WebP.');
        return;
      }

      if (file.size > maxSize) {
        setError('Fichier trop volumineux. Maximum 5MB.');
        return;
      }

      setSelectedImage(file);
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setSelectedImage(null);
    setImagePreview('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    
    
    setLoading(true);
    setError('');

    try {
      // ✅ NOUVEAU : Validation selon le type de produit
      if (!formData.name.trim()) {
        setError('Le nom du produit est requis');
        setLoading(false);
        return;
      }

      if (!formData.price || parseFloat(formData.price) <= 0) {
        setError('Le prix doit être supérieur à 0');
        setLoading(false);
        return;
      }

      if (!formData.categoryName.trim()) {
        setError('Veuillez sélectionner ou saisir une catégorie');
        setLoading(false);
        return;
      }

      // ✅ NOUVEAU : Validation spécifique aux produits stockables
      if (formData.productType === PRODUCT_TYPES.STOCKABLE) {
        if (formData.stock && parseInt(formData.stock) < 0) {
          setError('Le stock ne peut pas être négatif');
          setLoading(false);
          return;
        }
        
        const minThreshold = parseInt(formData.minThreshold) || 0;
        const maxThreshold = parseInt(formData.maxThreshold) || 0;
        
        if (minThreshold < 0 || maxThreshold < 0) {
          setError('Les seuils ne peuvent pas être négatifs');
          setLoading(false);
          return;
        }
        
        if (maxThreshold > 0 && minThreshold >= maxThreshold) {
          setError('Le seuil minimum doit être inférieur au maximum');
          setLoading(false);
          return;
        }
      }

      // Format différent selon création/modification
      let productData;
      
      if (isEditing) {
  // MODIFICATION : Format UpdateProductRequest avec la bonne structure
  const selectedCategoryForUpdate = selectedCategory || categories?.find(cat => 
    cat.name === formData.categoryName
  );

  if (!selectedCategoryForUpdate) {
    setError('Catégorie non trouvée. Veuillez sélectionner une catégorie valide.');
    setLoading(false);
    return;
  }

  // ✅ CORRECTION: Structure exacte attendue par UpdateProductRequest.java
  productData = {
    name: formData.name.trim(),
    description: formData.description?.trim() || '',
    sellPrice: parseFloat(formData.price), // ⚠️ ATTENTION: sellPrice dans UpdateProductRequest
    costPrice: formData.costPrice ? parseFloat(formData.costPrice) : 0,
    sku: formData.sku?.trim() || null,
    barcode: formData.barcode?.trim() || null,
    categoryId: selectedCategoryForUpdate.id,
    // ✅ CORRECTION: Toujours envoyer le type de produit
    productType: formData.productType,
    isActive: true,
    imageUrl: product?.imageUrl || null // ✅ Préserver l'image existante
  };

  // ✅ CORRECTION: Ajouter les champs de stock UNIQUEMENT pour les produits stockables
  // ET avec les bonnes valeurs (pas null si vide)
  if (formData.productType === PRODUCT_TYPES.STOCKABLE) {
    productData.currentStock = formData.stock ? parseInt(formData.stock) : 0;
    productData.minThreshold = formData.minThreshold ? parseInt(formData.minThreshold) : 5;
    productData.maxThreshold = formData.maxThreshold ? parseInt(formData.maxThreshold) : 50;
    
    
  }
  // Pour les services, ne pas ajouter ces champs du tout

}
       else {
        // CRÉATION : Format CreateProductRequest original avec categoryName
        productData = {
          name: formData.name.trim(),
          description: formData.description?.trim() || '',
          price: parseFloat(formData.price),
          categoryName: formData.categoryName.trim(), // Création auto côté backend
          sku: formData.sku?.trim() || null,
          barcode: formData.barcode?.trim() || null,
          costPrice: formData.costPrice ? parseFloat(formData.costPrice) : null,
          productType: formData.productType, // ✅ NOUVEAU
          initialStock: formData.productType === PRODUCT_TYPES.STOCKABLE && formData.stock ? parseInt(formData.stock) : null,
          minThreshold: formData.productType === PRODUCT_TYPES.STOCKABLE && formData.minThreshold ? parseInt(formData.minThreshold) : null,
          maxThreshold: formData.productType === PRODUCT_TYPES.STOCKABLE && formData.maxThreshold ? parseInt(formData.maxThreshold) : null,
          imageUrl: product?.imageUrl || null // ✅ Préserver l'image existante

        };
      }


      let response;
      if (isEditing) {
        response = await productService.updateProduct(product.id, productData);
      } else {
        response = await productService.createProduct(productData);
      }


      if (response.success) {
        
        // Upload de l'image si une image est sélectionnée
        if (selectedImage) {
          const productId = isEditing ? product.id : response.data?.id;
          if (productId) {
            
            const uploadResponse = await productService.uploadProductImage(
              productId, 
              selectedImage
            );
            
            if (uploadResponse.success) {
            } else {
              console.warn('⚠️ Échec upload image:', uploadResponse.message);
            }
          }
        }
        
        onSuccess?.();
        onHide();
        
        // Recharger les catégories si une nouvelle a été créée
        if (!selectedCategory && formData.categoryName.trim()) {
        }
      } else {
        setError(response.message || 'Erreur lors de la sauvegarde');
      }

    } catch (error) {
      console.error('❌ Erreur capturée:', error);
      setError(error.response?.data?.message || error.message || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          {formData.productType === PRODUCT_TYPES.STOCKABLE ? (
            <Package size={20} />
          ) : (
            <Briefcase size={20} />
          )}
          {isEditing ? 'Modifier le produit' : 'Nouveau produit'}
          <Badge bg={formData.productType === PRODUCT_TYPES.STOCKABLE ? 'primary' : 'info'}>
            {formData.productType === PRODUCT_TYPES.STOCKABLE ? 'Stockable' : 'Service'}
          </Badge>
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" className="mb-3">
              {error}
            </Alert>
          )}

          {(!categories || categories.length === 0) && (
            <Alert variant="warning" className="mb-3">
              <strong>Attention :</strong> Aucune catégorie disponible. 
              La première catégorie sera créée automatiquement.
            </Alert>
          )}

          {/* ✅ NOUVEAU : Sélection du type de produit - Plus compact */}
          { user.productAccessType === 'ALL' && (
          <Form.Group className="mb-4">
            <Form.Label className="fw-bold">Type de produit</Form.Label>
            <div className="d-flex flex-wrap gap-2 mt-2">
              <Form.Check
                type="radio"
                id="type-stockable"
                name="productType"
                value={PRODUCT_TYPES.STOCKABLE}
                checked={formData.productType === PRODUCT_TYPES.STOCKABLE}
                onChange={(e) => handleProductTypeChange(e.target.value)}
                label={
                  <div className="d-flex align-items-center gap-2">
                    <Package size={16} />
                    <div>
                      <strong>Stockable</strong>
                      <div className="text-muted small d-none d-sm-block">Produit avec stock</div>
                    </div>
                  </div>
                }
              />
              <Form.Check
                type="radio"
                id="type-service"
                name="productType"
                value={PRODUCT_TYPES.SERVICE}
                checked={formData.productType === PRODUCT_TYPES.SERVICE}
                onChange={(e) => handleProductTypeChange(e.target.value)}
                label={
                  <div className="d-flex align-items-center gap-2">
                    <Briefcase size={16} />
                    <div>
                      <strong>Service</strong>
                      <div className="text-muted small d-none d-sm-block">Sans stock</div>
                    </div>
                  </div>
                }
              />
            </div>
          </Form.Group>
          )}


          {/* ✅ Section Image - EXACTEMENT comme l'original */}
          <Form.Group className="mb-3">
            <Form.Label>Image du produit</Form.Label>
            <div className="border rounded p-3 text-center">
              {imagePreview ? (
                <div className="position-relative d-inline-block">
                  <img 
                    src={imagePreview} 
                    alt="Aperçu" 
                    className="img-thumbnail"
                    style={{ maxWidth: '200px', maxHeight: '150px' }}
                  />
                  <Button
                    variant="danger"
                    size="sm"
                    className="position-absolute top-0 end-0"
                    onClick={removeImage}
                    style={{ transform: 'translate(50%, -50%)' }}
                  >
                    <X size={12} />
                  </Button>
                </div>
              ) : (
                <div className="py-4">
                  <ImageIcon size={48} className="text-muted mb-2" />
                  <p className="text-muted mb-0">Aucune image sélectionnée</p>
                </div>
              )}
              
              <div className="mt-3">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ display: 'none' }}
                  id="imageInput"
                />
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => document.getElementById('imageInput').click()}
                >
                  <Upload size={16} className="me-2" />
                  {imagePreview ? 'Changer l\'image' : 'Sélectionner une image'}
                </Button>
              </div>
              
              <small className="text-muted d-block mt-2">
                JPG, PNG, WebP • Max 5MB
              </small>
            </div>
          </Form.Group>

          {/* Informations de base - Responsive comme avant */}
          <Row>
            <Col xs={12} md={8}>
              <Form.Group className="mb-3">
                <Form.Label>
                  <Tag size={16} className="me-1" />
                  Nom du produit *
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Ex: T-shirt bleu, Consultation, etc."
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={4}>
              <Form.Group className="mb-3">
                <Form.Label>SKU</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Code produit"
                  value={formData.sku}
                  onChange={(e) => setFormData(prev => ({ ...prev, sku: e.target.value }))}
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Description détaillée..."
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            />
          </Form.Group>

          {/* Catégorie avec autocomplétion */}
          <Form.Group className="mb-3" style={{ position: 'relative' }}>
            <Form.Label>
              <Tag size={16} className="me-1" />
              Catégorie *
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Tapez ou sélectionnez une catégorie..."
              value={formData.categoryName}
              onChange={(e) => handleCategoryInputChange(e.target.value)}
              onFocus={() => {
                if (filteredCategories.length > 0) {
                  setShowCategorySuggestions(true);
                }
              }}
              required
            />
            
            {showCategorySuggestions && filteredCategories.length > 0 && (
              <Card className="position-absolute w-100" style={{ zIndex: 1050, top: '100%' }}>
                <ListGroup variant="flush">
                  {filteredCategories.map((category, index) => (
                    <ListGroup.Item 
                      key={category.id}
                      action
                      onClick={() => selectCategory(category)}
                      className="d-flex align-items-center gap-2"
                    >
                      <span style={{ fontSize: '1.2em' }}>{category.icon || '📦'}</span>
                      <span>{category.name}</span>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Card>
            )}
          </Form.Group>

          {/* Prix - Responsive */}
          <Row>
            <Col xs={12} sm={6}>
              <Form.Group className="mb-3">
                <Form.Label>Prix de vente (F CFA) *</Form.Label>
                <Form.Control
                  type="number"
                  onWheel={e => e.target.blur()} // Empêche le scroll de changer la valeur
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={formData.price}
                  onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                  required
                />
              </Form.Group>
            </Col>
            <Col xs={12} sm={6}>
              <Form.Group className="mb-3">
                <Form.Label>Prix de revient (F CFA)</Form.Label>
                <Form.Control
                  type="number"
                  onWheel={e => e.target.blur()} // Empêche le scroll de changer la valeur
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={formData.costPrice}
                  onChange={(e) => setFormData(prev => ({ ...prev, costPrice: e.target.value }))}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* ✅ NOUVEAU : Champs de stock conditionnels - Responsive */}
          {formData.productType === PRODUCT_TYPES.STOCKABLE && (
            <>
              <hr className="my-3" />
              <h6 className="mb-3 d-flex align-items-center gap-2">
                <Package size={18} />
                Gestion du stock
              </h6>
              
              <Row>
                <Col xs={12} sm={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Stock initial</Form.Label>
                    <Form.Control
                      type="number"
                      onAbort={e => e.target.blur()} // Empêche le scroll de changer la valeur
                      min="0"
                      placeholder="0"
                      value={formData.stock}
                      onChange={(e) => setFormData(prev => ({ ...prev, stock: e.target.value }))}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} sm={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Seuil minimum</Form.Label>
                    <Form.Control
                      type="number"
                      onWheel={e => e.target.blur()} // Empêche le scroll de changer la valeur
                      min="0"
                      placeholder="5"
                      value={formData.minThreshold}
                      onChange={(e) => setFormData(prev => ({ ...prev, minThreshold: e.target.value }))}
                    />
                    <Form.Text className="text-muted">Alerte de stock bas</Form.Text>
                  </Form.Group>
                </Col>
                <Col xs={12} sm={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Seuil maximum</Form.Label>
                    <Form.Control
                      type="number"
                      onWheel={e => e.target.blur()} // Empêche le scroll de changer la valeur
                      min="0"
                      placeholder="50"
                      value={formData.maxThreshold}
                      onChange={(e) => setFormData(prev => ({ ...prev, maxThreshold: e.target.value }))}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </>
          )}

          {/* ✅ NOUVEAU : Message informatif pour les services - Compact */}
          {formData.productType === PRODUCT_TYPES.SERVICE && (
            <Alert variant="info" className="mt-2 mb-3">
              <div className="d-flex align-items-center gap-2">
                <Briefcase size={16} />
                <span><strong>Service :</strong> Aucune gestion de stock ne sera appliquée</span>
              </div>
            </Alert>
          )}
          {/* Code-barres */}
          <Form.Group className="mb-3">
            <Form.Label>Code-barres</Form.Label>
            <Form.Control
              type="text"
              placeholder="Code-barres du produit (optionnel)"
              value={formData.barcode}
              onChange={(e) => setFormData(prev => ({ ...prev, barcode: e.target.value }))}
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={loading}>
            Annuler
          </Button>
          <Button type="submit" variant="primary" disabled={loading}>
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                {isEditing ? 'Modification...' : 'Création...'}
              </>
            ) : (
              isEditing ? 'Modifier le produit' : 'Créer le produit'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CreateProductModal;